import { useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase } from '../lib/typeHelper'
import { orgParams } from './coi'
import { EmailAttachmentFilter, type EmailThread, type EmailThreadPage } from './internalEmail'

/** One org's certificate mail, threaded — the COI page's Emails tab. */
export interface COIEmailThreadPage extends EmailThreadPage {
  /** Threads a human still owes an answer, whatever the current filter. */
  needsAttentionCount: number
  needsAttentionKeys: string[]
}

export interface COIEmailFilters {
  q?: string
  hasAttachments?: EmailAttachmentFilter
  needsAttention?: boolean
  page?: number
}

function getCOIEmailThreads(filters: COIEmailFilters, orgId?: string): Promise<COIEmailThreadPage> {
  const params: Record<string, string> = {}
  if (orgId) params.org_id = orgId
  if (filters.q) params.q = filters.q
  if (filters.hasAttachments && filters.hasAttachments !== EmailAttachmentFilter.ANY) {
    params.has_attachments = filters.hasAttachments
  }
  if (filters.needsAttention) params.needs_attention = 'true'
  if (filters.page && filters.page > 1) params.page = String(filters.page)
  return axios
    .get('/api/v1/insure/coi-emails/threads', { params })
    .then((r) => toCamelCase(r.data) as COIEmailThreadPage)
}

export function useCOIEmailThreads(filters: COIEmailFilters, orgId?: string) {
  return useQuery({
    queryKey: [CacheKeys.COI_EMAIL_THREADS, orgId, filters],
    queryFn: () => getCOIEmailThreads(filters, orgId),
    // Keeps the list on screen while a filter change is in flight, so toggling
    // "Needs attention" doesn't blank the pane.
    placeholderData: (previous) => previous,
  })
}

function getCOIEmailThread(threadKey: string, orgId?: string): Promise<EmailThread> {
  return axios
    .get(`/api/v1/insure/coi-emails/threads/${encodeURIComponent(threadKey)}`, {
      params: orgParams(orgId),
    })
    .then((r) => toCamelCase(r.data) as EmailThread)
}

export function useCOIEmailThread(threadKey: string | null, orgId?: string) {
  return useQuery({
    queryKey: [CacheKeys.COI_EMAIL_THREAD, orgId, threadKey],
    queryFn: () => getCOIEmailThread(threadKey as string, orgId),
    enabled: !!threadKey,
  })
}
